import { useCallback, useEffect, useState, type FC } from "react";
import { ComponentBaseContainer, ComponentContainer, StyledCopyIcon } from "../jsonformatter/JsonFormatter.style";
import SideNav from "../nav/SideNav";
import AdsComponent from "../AdsComponent";
import { CronContainer } from "../crongenerator/CronGenerator.style";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import { v4 as uuidv4, v5 as uuidv5, v7 as uuidv7, validate as validateUUID } from "uuid";
import Chip from "@mui/material/Chip";
import { Helmet } from "react-helmet-async";
import Switch from "@mui/material/Switch";

const versions = ["V4", "V5", "V7"] as const;
type UUIDVersion = typeof versions[number];

const UuidGenerator: FC = () => {
    const [selectedVersion, setSelectedVersion] = useState<UUIDVersion>("V4");
    const [uuid, setUuid] = useState<string>("");
    const [name, setName] = useState<string>("");
    const [namespace, setNamespace] = useState<string>("");
    const [isValidNamespace, setIsValidNamespace] = useState<boolean>(false);
    const [showToast, setShowToast] = useState<boolean>(false);
    const [isHyphned, setIsHypned] = useState<boolean>(true);
    const [uuidCase, setUuidCase] = useState<string>("lower");

    useEffect(() => {
        if (selectedVersion === "V5") {
            setIsValidNamespace(!!name && validateUUID(namespace));
        }
    }, [name, namespace, selectedVersion]);

    const generateUUID = useCallback(() => {
        try {
            let generated = "";
            switch (selectedVersion) {
                case "V4":
                    generated = uuidv4();
                    break;
                case "V5":
                    if (isValidNamespace) {
                        generated = uuidv5(name, namespace);
                    }
                    break;
                case "V7":
                    generated = uuidv7();
                    break;
            }
            setUuid(generated);
        } catch {
            setUuid("Invalid input");
        }
    }, [selectedVersion, isValidNamespace, name, namespace]);

    useEffect(() => {
        generateUUID();
    }, [selectedVersion, generateUUID]);

    const handleCopy = () => {
        uuid && navigator.clipboard.writeText(uuid)
            .then(() => {
                setShowToast(true);
                setTimeout(() => setShowToast(false), 1000);
            })
    }

    const handleCaseChange = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        checked ? setUuidCase("upper") : setUuidCase("lower")
    }

    const handleHyphenChange = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        checked ? setIsHypned(false) : setIsHypned(true)
    }

    const renderUUID = () => {
        let renderUuid = isHyphned ? uuid : uuid.replace(/-/g, '');
        return uuidCase === "lower" ? renderUuid.toLowerCase() : renderUuid.toUpperCase()
    }

    return (
        <ComponentBaseContainer>
            <Helmet>
                <title>UUID Generator Online | Create UUIDv4, UUIDv5, UUIDv7</title>
                <meta name="description" content="Instantly generate UUIDs of different versions (V4, V5, V7) online. Free and privacy-friendly developer tool." />
                <meta name="keywords" content="uuid generator, uuidv4, uuidv5, uuidv7, online uuid, generate uuid" />
            </Helmet>

            <SideNav />
            <ComponentContainer>
                <CronContainer>
                    <h1>UUID GENERATOR</h1>

                    <ButtonGroup variant="text" aria-label="UUID version selector" color="inherit">
                        {versions.map((version) => (
                            <Button
                                key={version}
                                color={selectedVersion === version ? "info" : "inherit"}
                                onClick={() => setSelectedVersion(version)}
                            >
                                {version}
                            </Button>
                        ))}
                    </ButtonGroup>

                    <Divider style={{ color: "black", width: "90%", margin: "20px 0" }} />
                    <div style={{
                        display: "flex",
                        flexDirection: "column",
                        height: "40%",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        width: "100%"
                    }}>
                        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%" }}>
                            <h3 style={{margin:"10px", overflowWrap:"anywhere"}}>{renderUUID()}</h3>
                            {uuid && <StyledCopyIcon onClick={handleCopy} style={{ margin: "0 5px" }} />}
                        </div>
                        {selectedVersion === "V5" && (
                            <div style={{
                                display: "flex",
                                flexDirection: "column",
                                width: "65%"
                            }}>
                                <TextField
                                    required
                                    variant="standard"
                                    sx={{ m: 1, width: "100%" }}
                                    label="Name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                />
                                <TextField
                                    required
                                    variant="standard"
                                    sx={{ m: 1, width: "100%" }}
                                    label="Namespace"
                                    value={namespace}
                                    error={namespace !== "" && !validateUUID(namespace)}
                                    helperText={
                                        namespace && !validateUUID(namespace)
                                            ? "Invalid UUID format"
                                            : " "
                                    }
                                    onChange={(e) => setNamespace(e.target.value)}
                                />
                            </div>
                        )}
                        {selectedVersion !== "V5" && <Chip label="Generate UUID" variant="outlined" onClick={generateUUID} style={{ marginTop: "10px", width: "50%" }} />}


                    </div>


                </CronContainer>

                <Divider orientation="vertical" />

                <CronContainer>
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <div>lowercase</div>
                        <Switch color="default" onChange={handleCaseChange} />
                        <div>UPPERCASE</div>
                    </div>
                    <Divider style={{ color: "black", width: "90%", margin: "20px 0" }} />
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <div>With hyphens</div>
                        <Switch color="default" onChange={handleHyphenChange} />
                        <div>Without hyphens</div>
                    </div>
                </CronContainer>
                {showToast && (
                    <div style={{
                        position: "absolute",
                        top: "10px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        background: "#323232",
                        color: "white",
                        padding: "8px 16px",
                        borderRadius: "6px",
                        opacity: 0.9,
                        fontSize: "14px",
                    }}>
                        Copied to clipboard!
                    </div>
                )}
            </ComponentContainer>

            <AdsComponent />
        </ComponentBaseContainer>
    );
};

export default UuidGenerator;
